import { eq, useLiveQuery } from "@tanstack/react-db";
import { SmartContextsCollection } from "@/domains/tanstack-db/core/collections";
import { TanstackBaseRepository, TanstackRepositoryArgs } from "../base/repository";
import { smartContextRecordSchema, smartContextUpsertSchema } from "./schemas";
import { SmartContextRow, SmartContextUpsertInput } from "./schemas";

export class SmartContextRepository extends TanstackBaseRepository<SmartContextsCollection> {
  constructor({ collection }: TanstackRepositoryArgs<SmartContextsCollection>) {
    super({
      collection,
    });
  }

  async upsertContext(params: SmartContextUpsertInput): Promise<void> {
    const input = smartContextUpsertSchema.parse(params);
    const existing = this.collection.get(input.id) ?? null;
    const nowIso = new Date().toISOString();

    const record = smartContextRecordSchema.parse({
      id: input.id,
      associated_space_account_id: input.associated_space_account_id,
      payload_json:
        input.payload_json !== undefined ? input.payload_json : (existing?.payload_json ?? null),
      created_at: input.created_at ?? existing?.created_at ?? nowIso,
      updated_at: input.updated_at ?? nowIso,
    });

    if (existing) {
      const tx = this.collection.update(record.id, draft => {
        Object.assign(draft, record);
      });
      await tx.isPersisted.promise;
      return;
    }

    const tx = this.collection.insert(record);
    await tx.isPersisted.promise;
  }

  useById({ contextId }: { contextId: string | null }): {
    context: SmartContextRow | null;
    isLoading: boolean;
    error: string | null;
  } {
    const isEnabled = Boolean(contextId);

    const queryResult = useLiveQuery(
      q => {
        if (!isEnabled) return undefined;

        return q
          .from({ smartContexts: this.collection })
          .where(({ smartContexts }) => eq(smartContexts.id, contextId))
          .findOne();
      },
      [contextId]
    );

    const context = queryResult.data ?? null;
    const error = isEnabled && queryResult.status === "error" ? "Failed to load context." : null;

    return {
      context: isEnabled ? context : null,
      isLoading: isEnabled ? queryResult.isLoading : false,
      error,
    };
  }
}
